/* eslint-disable react/prop-types */
import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import './DprPipelineSelect.css';

/**
 * Pipeline / mainline picker — same trigger + dropdown pattern as DPR Settings selects.
 */
const DprPipelineSelect = ({
  value,
  options = [],
  onChange,
  placeholder = 'Select pipeline',
  disabled = false,
  className = '',
  appearance = 'pill',
  searchable = false,
  'aria-label': ariaLabel,
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef(null);

  const selected = options.find((o) => String(o.value) === String(value));
  const displayLabel = selected?.label ?? '';

  const filteredOptions = searchable && searchTerm.trim()
    ? options.filter((opt) =>
        String(opt.label || '').toLowerCase().includes(searchTerm.trim().toLowerCase())
      )
    : options;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        setSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!open) setSearchTerm('');
  }, [open]);

  const triggerClass =
    appearance === 'field'
      ? 'dpr-pipeline-select-trigger dpr-pipeline-select-trigger--field'
      : 'dpr-pipeline-select-trigger';

  return (
    <div
      ref={containerRef}
      className={`dpr-pipeline-select-wrap dpr-pipeline-select-wrap--${appearance} ${className}`.trim()}
    >
      <button
        type="button"
        className={triggerClass}
        onClick={() => !disabled && setOpen((prev) => !prev)}
        disabled={disabled}
        aria-label={ariaLabel || placeholder}
        aria-expanded={open}
      >
        <span className={`dpr-pipeline-select-value${displayLabel ? '' : ' dpr-pipeline-select-value--placeholder'}`}>
          {displayLabel || placeholder}
        </span>
        {open ? (
          <ChevronUp size={16} color="#6B7280" aria-hidden />
        ) : (
          <ChevronDown size={16} color="#6B7280" aria-hidden />
        )}
      </button>
      {open && (
        <div className="dpr-pipeline-select-dropdown" role="listbox">
          {searchable ? (
            <div className="dpr-pipeline-select-search">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search..."
                className="dpr-pipeline-select-search__input"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          ) : null}
          {filteredOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              role="option"
              aria-selected={String(opt.value) === String(value)}
              className="dpr-pipeline-select-opt"
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
            >
              {opt.label}
            </button>
          ))}
          {filteredOptions.length === 0 ? (
            <div className="dpr-pipeline-select-empty">No matches</div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default DprPipelineSelect;
